import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { IXsollaPricingResponse } from '../../../common/xsolla';
import { assertValidXsollaDisplayPricing } from '../../../common/xsollaDisplayPricing';

type TFetchXsollaPricing = (params: { market?: string }) => Promise<IXsollaPricingResponse & { success: boolean }>;

type TUseXsollaPricingOptions = {
  enabled: boolean;
  market?: string;
  fetchPricing: TFetchXsollaPricing;
};

function hasCompletePreferredCurrencyPricing(
  plans: IXsollaPricingResponse['plans'],
  preferredCurrency?: string | null
): boolean {
  const normalizedPreferredCurrency = String(preferredCurrency || '')
    .trim()
    .toUpperCase();
  if (!normalizedPreferredCurrency) {
    return true;
  }

  return Object.values(plans).every(
    ({ monthly, annual }) =>
      monthly.currency.toUpperCase() === normalizedPreferredCurrency &&
      annual.currency.toUpperCase() === normalizedPreferredCurrency
  );
}

function buildBaseCurrencyPricing(plans: IXsollaPricingResponse['plans']): IXsollaPricingResponse['plans'] {
  return Object.fromEntries(
    Object.entries(plans).map(([tier, { monthly, annual }]) => [
      tier,
      {
        monthly: {
          ...monthly,
          amount: monthly.baseAmount,
          currency: monthly.baseCurrency,
          isLocalized: false
        },
        annual: {
          ...annual,
          amount: annual.baseAmount,
          currency: annual.baseCurrency,
          isLocalized: false
        }
      }
    ])
  ) as IXsollaPricingResponse['plans'];
}

export function useXsollaPricing(options: TUseXsollaPricingOptions) {
  const { enabled, market, fetchPricing } = options;
  const [pricing, setPricing] = useState<IXsollaPricingResponse['plans'] | null>(null);
  const [pricingInfo, setPricingInfo] = useState<string | null>(null);
  const [isPricingLoading, setIsPricingLoading] = useState(false);
  const [isPricingUnavailable, setIsPricingUnavailable] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setPricing(null);
      setPricingInfo(null);
      setIsPricingLoading(false);
      setIsPricingUnavailable(false);
      return;
    }

    let isCancelled = false;
    setIsPricingLoading(true);
    setPricing(null);
    setPricingInfo(null);
    setIsPricingUnavailable(false);

    fetchPricing({ market })
      .then((result) => {
        if (isCancelled) {
          return;
        }

        if (!result.success) {
          throw new Error('Failed to load Xsolla pricing');
        }

        assertValidXsollaDisplayPricing(result.plans);
        const hasCompletePreferredCurrency = hasCompletePreferredCurrencyPricing(
          result.plans,
          result.preferredCurrency
        );
        const displayPricing = hasCompletePreferredCurrency ? result.plans : buildBaseCurrencyPricing(result.plans);

        setPricing(displayPricing);
        setIsPricingUnavailable(false);
        if (result.preferredCurrency && (!result.hasLocalizedPricing || !hasCompletePreferredCurrency)) {
          setPricingInfo(t('xsolla:final-price-at-checkout'));
          return;
        }

        setPricingInfo(null);
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }

        console.error('Failed to load Xsolla pricing:', error);
        setPricing(null);
        setPricingInfo(t('xsolla:pricing-unavailable-at-checkout'));
        setIsPricingUnavailable(true);
      })
      .finally(() => {
        if (!isCancelled) {
          setIsPricingLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [enabled, fetchPricing, market]);

  return {
    pricing,
    pricingInfo,
    isPricingLoading: isPricingLoading || (enabled && !pricing && !isPricingUnavailable),
    isPricingUnavailable
  };
}
